import { useRef, useState } from 'react'
import { useKeyboardUp } from '@/hooks/useVisualViewport'
import { deleteWithUndo } from '@/lib/actions'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import {
  Check,
  ChevronDown,
  ChevronUp,
  Eraser,
  FolderInput,
  ListChecks,
  Pin,
  Repeat2,
  RotateCcw,
  Trash2,
} from 'lucide-react'
import { mutate } from '@/runtime'
import { useAllPages, useAllProjects, useProjectOf, usePeople, useRecord, useSettings } from '@/db/hooks'
import type { ListResource } from '@/kinds/list/apply'
import type { ListItem, ListSpec, ListStatus } from '@/kinds/list/schema'
import { ListForm } from '@/kinds/list/components'
import { BecameBanner, ConvertSheet } from '@/components/ConvertSheet'
import { sortPages } from '@/kinds/page/components'
import { MoveSheet } from './page/MoveSheet'
import { RemindPicker, fmtRemind } from '@/components/RemindPicker'
import { Bell } from 'lucide-react'
import { Icon } from '@/lib/icons'
import { labelStyle, personHue, tintText, tokens } from '@/lib/colour'
import { firstOf, personFor } from '@/lib/people'
import { Avatar } from '@/components/PersonBits'
import type { PersonResource } from '@/kinds/person/apply'
import { useUi } from '@/state/ui'
import { addItemsArgs, splitItems } from '@/kinds/list/items'
import { ActionBar, type ActionItem } from '@/components/ActionBar'
import { useNudgeMembersItem } from '@/components/NudgeMembersButton'
import { useShareWithItem } from '@/components/ShareWith'
import { Button, Sheet, Tag, cx } from '@/components/ui'
import { Gone } from './NotFound'

/**
 * One list, built for a phone in a shop: big tap targets to tick, an add box
 * that stays at the bottom, ticked items gathered underneath. Every change is
 * a named action so another phone working the same list never gets clobbered.
 */
export const ListView = () => {
  const { id } = useParams({ strict: false }) as { id: string }
  const list = useRecord<ListSpec, ListStatus>(id) as ListResource | null | undefined
  const navigate = useNavigate()
  const keyboardUp = useKeyboardUp()
  const flash = useUi((s) => s.showFlash)
  const settings = useSettings()
  const people = usePeople()
  const parentOf = useProjectOf()
  const projects = useAllProjects() ?? []
  const pages = useAllPages() ?? []
  const [editing, setEditing] = useState(false)
  const [moving, setMoving] = useState(false)
  const [converting, setConverting] = useState(false)
  const [item, setItem] = useState<ListItem | null>(null)
  const [draft, setDraft] = useState('')
  const addRef = useRef<HTMLInputElement>(null)

  const nudgeItem = useNudgeMembersItem(id, list?.labels.ws)
  const shareWithItem = useShareWithItem(
    list ? { kind: 'List', id: list.id, title: list.spec.title, ws: list.labels.ws } : null,
  )

  if (list === undefined) return null
  if (!list || list.deleted_at) return <Gone kind="list" deleted={!!list?.deleted_at} />

  const open = list.spec.items.filter((i) => !i.done)
  const done = list.spec.items.filter((i) => i.done)
  const act = (action: string, args?: unknown) =>
    mutate.action(list.id, action, args).catch((e: Error) => flash(e.message, 2500))

  const add = async () => {
    // one item per line, so a pasted list lands as items
    const texts = splitItems(draft)
    if (!texts.length) return
    setDraft('')
    await act('add_items', addItemsArgs(texts))
    addRef.current?.focus()
  }
  const remove = async () => {
    await deleteWithUndo(list.id, list.spec.title)
    void navigate({ to: '/lists' })
  }
  const actions: ActionItem[] = [
    {
      id: 'pin',
      label: list.spec.pinned ? 'Pinned' : 'Pin',
      icon: Pin,
      pin: true,
      pressed: !!list.spec.pinned,
      onSelect: () => void mutate.patch(list.id, { spec: { pinned: !list.spec.pinned } }),
      testid: 'list-pin',
    },
    {
      id: 'reset',
      label: 'Reset',
      icon: RotateCcw,
      hidden: !(done.length > 0 && list.spec.style === 'reusable'),
      onSelect: () => void act('reset'),
      testid: 'list-reset',
    },
    {
      id: 'clear',
      label: 'Clear ticked',
      icon: Eraser,
      hidden: !(done.length > 0 && list.spec.style !== 'reusable'),
      onSelect: () => void act('clear_ticked'),
      testid: 'list-clear',
    },
    {
      id: 'remind',
      label: 'Remind me',
      pin: true,
      render: ({ as }) => (
        <RemindPicker
          as={as}
          value={list.spec.remind_at}
          fired={!!list.spec.remind_at && list.status.reminder_fired_for === list.spec.remind_at}
          onChange={(v) => void mutate.patch(list.id, { spec: { remind_at: v } })}
        />
      ),
    },
    { id: 'move', label: 'Move…', icon: FolderInput, onSelect: () => setMoving(true), testid: 'list-move' },
    nudgeItem,
    shareWithItem,
    {
      id: 'convert',
      label: 'Convert to…',
      icon: Repeat2,
      onSelect: () => setConverting(true),
      testid: 'list-convert',
    },
    {
      id: 'delete',
      label: 'Delete',
      tone: 'danger',
      onSelect: () => void remove(),
      confirm: { label: 'Delete list', testid: 'list-delete-confirm' },
      testid: 'list-delete',
    },
  ]
  const parent = parentOf.get(list.id) ?? null
  const sameWs = (r: { labels: Record<string, string> }) => r.labels.ws === list.labels.ws
  const back = parent
    ? projects.find((p) => p.id === parent)
      ? {
          to: '/projects/$id' as const,
          params: { id: parent },
          label: projects.find((p) => p.id === parent)!.spec.title,
        }
      : pages.find((p) => p.id === parent)
        ? {
            to: '/pages/$id' as const,
            params: { id: parent },
            label: pages.find((p) => p.id === parent)!.name,
          }
        : null
    : null

  return (
    <div data-testid="list-view" className="pb-24 md:pb-0">
      <div className="flex items-center justify-between text-sm text-muted mb-3">
        {back ? (
          <Link to={back.to} params={back.params} className="hover:text-fg">
            ← {back.label}
          </Link>
        ) : (
          <Link to="/lists" className="hover:text-fg">
            ← Lists
          </Link>
        )}
      </div>
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="flex items-center gap-3 text-left mb-3 rounded-xl -mx-1 px-1 hover:bg-raised"
        data-testid="list-title"
      >
        <span className="text-3xl leading-none">
          <Icon value={list.spec.icon} size={30} fallback={<ListChecks size={28} className="text-muted" />} />
        </span>
        <span>
          <span className="block text-2xl font-semibold tracking-tight">{list.spec.title}</span>
          <span className="block text-xs text-faint">
            {list.spec.style === 'reusable' ? 'Reusable list' : 'Checklist'}
          </span>
        </span>
      </button>

      <ActionBar className="mb-4" items={actions} label="List actions" testid="list-actions" />

      {list.status.converted_to && (
        <BecameBanner
          to={list.status.converted_to}
          titleOf={(id) =>
            projects.find((p) => p.id === id)?.spec.title ?? pages.find((p) => p.id === id)?.name
          }
        />
      )}
      {list.spec.items.length === 0 ? (
        <p className="text-sm text-faint py-6 text-center">Nothing on it yet. Add the first thing below.</p>
      ) : (
        <>
          <ul className={open.length ? 'group-list' : ''} data-testid="list-open">
            {open.map((it) => (
              <ItemRow
                key={it.id}
                it={it}
                people={people}
                labelStyles={settings.label_styles}
                onToggle={() => void act(it.done ? 'untick' : 'tick', { id: it.id })}
                onEdit={() => setItem(it)}
              />
            ))}
          </ul>
          {done.length > 0 && (
            <>
              <h2 className="text-[13px] font-semibold text-muted mt-5 mb-1.5">Ticked</h2>
              <ul className="group-list" data-testid="list-done">
                {done.map((it) => (
                  <ItemRow
                    key={it.id}
                    it={it}
                    people={people}
                    labelStyles={settings.label_styles}
                    onToggle={() => void act(it.done ? 'untick' : 'tick', { id: it.id })}
                    onEdit={() => setItem(it)}
                  />
                ))}
              </ul>
            </>
          )}
        </>
      )}

      {/* add box: fixed above the tab bar on the phone, inline on desktop */}
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void add()
        }}
        className="kb-follow fixed inset-x-3 z-20 md:static md:mt-4 md:inset-auto flex gap-2 rounded-full md:rounded-xl bg-panel/95 backdrop-blur-md border border-line p-1.5 shadow-[var(--shadow-pop)] md:shadow-none"
        // above the tab bar normally; above the keyboard while typing into it
        style={{
          // the tab bar steps aside while typing, so its height is not a floor then
          bottom: keyboardUp ? 'calc(var(--vv-gap) + 0.5rem)' : 'calc(var(--above-tabbar) + 0.75rem)',
        }}
      >
        <input
          ref={addRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add an item…"
          className="bare-input flex-1 min-w-0 px-3"
          autoCapitalize="sentences"
          enterKeyHint="done"
          data-testid="list-add-input"
        />
        <Button
          variant="primary"
          type="submit"
          className="!rounded-full"
          disabled={!draft.trim()}
          data-testid="list-add-submit"
        >
          Add
        </Button>
      </form>

      {editing && <ListForm resource={list} onDone={() => setEditing(false)} />}
      {converting && <ConvertSheet source={list} tasks={[]} onClose={() => setConverting(false)} />}
      {moving && (
        <MoveSheet
          parent={parent}
          projects={projects.filter(
            (p) => sameWs(p) && p.status.state !== 'done' && p.status.state !== 'dropped',
          )}
          pages={sortPages(pages.filter((p) => sameWs(p) && !p.deleted_at))}
          onMove={async (to) => {
            await mutate.setProject(list.id, to)
            setMoving(false)
          }}
          onClose={() => setMoving(false)}
        />
      )}
      {item && (
        <ItemSheet
          item={item}
          first={list.spec.items[0]?.id === item.id}
          last={list.spec.items[list.spec.items.length - 1]?.id === item.id}
          onClose={() => setItem(null)}
          onSave={async (text) => {
            await act('edit_item', { id: item.id, text })
            setItem(null)
          }}
          onRemove={async () => {
            await act('remove_item', { id: item.id })
            setItem(null)
          }}
          onRemind={async (when) => {
            await act('set_item_reminder', { id: item.id, remind_at: when ?? '' })
            setItem((cur) => (cur ? { ...cur, remind_at: when ?? undefined } : cur))
          }}
          onMove={async (dir) => {
            const ids = list.spec.items.map((i) => i.id)
            const i = ids.indexOf(item.id)
            const before = dir === 'up' ? ids[i - 1] : ids[i + 2]
            await act('move_item', { id: item.id, before: before ?? null })
          }}
        />
      )}
    </div>
  )
}

/**
 * Who added an item: the person by name with their face, as a PersonChip shows
 * them (not a link here, the row is already a button), else the handle as it came.
 */
const AddedBy = ({
  handle,
  people,
  labelStyles,
}: {
  handle: string
  people: PersonResource[]
  labelStyles: Parameters<typeof labelStyle>[2]
}) => {
  const person = personFor(people, handle)
  if (!person) return <Tag style={labelStyle('from', handle, labelStyles).style}>{handle}</Tag>
  const hue = personHue(person.spec.key)
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full pl-0.5 pr-2 h-5 text-[11px] leading-none"
      style={{ background: tokens(hue).soft, color: tintText(hue) }}
    >
      <Avatar person={person} size={16} />
      {firstOf(person)}
    </span>
  )
}

/** One item: a big tick target, the text (tap to edit), and who added it. */
const ItemRow = ({
  it,
  people,
  labelStyles,
  onToggle,
  onEdit,
}: {
  it: ListItem
  people: PersonResource[]
  labelStyles: Parameters<typeof labelStyle>[2]
  onToggle: () => void
  onEdit: () => void
}) => (
  <li
    className={cx('flex items-center gap-1 pl-1 pr-2', it.done && 'opacity-60')}
    data-list-item={it.id}
    data-done={it.done ? 'yes' : 'no'}
  >
    <button
      type="button"
      onClick={onToggle}
      className="h-12 w-12 shrink-0 rounded-full flex items-center justify-center active:scale-95 transition-transform"
      aria-label={it.done ? `Untick ${it.text}` : `Tick ${it.text}`}
      aria-pressed={it.done}
    >
      <span
        className={cx(
          'h-6 w-6 rounded-full border-2 flex items-center justify-center transition-colors',
          it.done ? 'bg-accent border-accent text-on-accent' : 'border-line-strong',
        )}
      >
        {it.done && <Check size={14} strokeWidth={3} aria-hidden />}
      </span>
    </button>
    <button
      type="button"
      onClick={onEdit}
      className="flex-1 min-w-0 text-left py-3 pr-1"
      aria-label={`Edit ${it.text}`}
    >
      <span className={cx('block text-[15px] leading-snug', it.done ? 'line-through text-muted' : 'text-fg')}>
        {it.text}
      </span>
      {(it.added_by || it.remind_at) && (
        <span className="mt-1 inline-flex items-center gap-1.5">
          {it.remind_at && !it.done && (
            <span
              className="inline-flex items-center gap-1 text-[11px] text-accent"
              data-testid="item-remind"
            >
              <Bell size={11} aria-hidden /> {fmtRemind(it.remind_at)}
            </span>
          )}
          {it.added_by && <AddedBy handle={it.added_by} people={people} labelStyles={labelStyles} />}
        </span>
      )}
    </button>
  </li>
)

/** Edit one item: text, order, remove. */
const ItemSheet = ({
  item,
  first,
  last,
  onClose,
  onSave,
  onRemove,
  onMove,
  onRemind,
}: {
  item: ListItem
  first: boolean
  last: boolean
  onClose: () => void
  onSave: (text: string) => Promise<void>
  onRemove: () => Promise<void>
  onMove: (dir: 'up' | 'down') => Promise<void>
  onRemind: (when: string | null) => Promise<void>
}) => {
  const [text, setText] = useState(item.text)
  return (
    <Sheet
      open
      onClose={onClose}
      title="Item"
      footer={
        <>
          <Button
            variant="chip"
            className="text-rose-300/80 mr-auto"
            onClick={() => void onRemove()}
            data-testid="item-remove"
          >
            <Trash2 size={14} aria-hidden /> Remove
          </Button>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!text.trim()}
            onClick={() => void onSave(text.trim())}
            data-testid="item-save"
          >
            Save
          </Button>
        </>
      }
    >
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="w-full mb-3"
        autoFocus
        data-testid="item-text"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && text.trim()) void onSave(text.trim())
        }}
      />
      <div className="flex flex-wrap gap-1.5">
        <RemindPicker value={item.remind_at} onChange={(v) => void onRemind(v)} />
        <Button variant="chip" disabled={first} onClick={() => void onMove('up')} aria-label="Move up">
          <ChevronUp size={14} aria-hidden /> Up
        </Button>
        <Button variant="chip" disabled={last} onClick={() => void onMove('down')} aria-label="Move down">
          <ChevronDown size={14} aria-hidden /> Down
        </Button>
      </div>
    </Sheet>
  )
}
